/**
 * Pure helpers for Scout Observe Context Analysis.
 * Keeps spend-vs-window semantics out of the React component so they can be unit-tested.
 */

export type RoundtripUsageLike = {
  turnNumber?: number | null;
  seq: number;
  roundtripIndex?: number | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  thinkingTokens?: number | null;
  cacheReadTokens?: number | null;
  cacheWriteTokens?: number | null;
  estimatedCostUsd?: number | null;
  model?: string | null;
  traceId?: string | null;
  systemPromptText?: string | null;
  toolDefinitionsJson?: string | null;
  systemPromptTokens?: number | null;
  toolDefinitionsTokens?: number | null;
  allowedSkillsJson?: string | null;
};

export type TurnRollupRow = {
  turnNumber: number;
  roundtrips: number;
  model: string | null;
  traceId: string | null;
  peakInputTokens: number;
  inputBilled: number;
  outputTokens: number;
  thinkingTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  estimatedCostUsd: number;
};

/** Cache read rate = cacheRead / totalInput (not read+write). */
export function cacheReadRatePct(cacheReadTokens: number, totalInputTokens: number): number | null {
  if (totalInputTokens <= 0) {
    return null;
  }
  return Math.round((cacheReadTokens / totalInputTokens) * 100);
}

/** Last roundtrip's input (= current context window) and peak across the conversation. */
export function windowStats(rows: RoundtripUsageLike[]): {
  latestInputTokens: number | null;
  peakInputTokens: number | null;
} {
  if (rows.length === 0) {
    return { latestInputTokens: null, peakInputTokens: null };
  }
  let peak = 0;
  let sawAny = false;
  for (const row of rows) {
    const input = row.inputTokens ?? 0;
    if (row.inputTokens != null) {
      sawAny = true;
      if (input > peak) {
        peak = input;
      }
    }
  }
  const last = rows[rows.length - 1];
  const latest = last?.inputTokens ?? null;
  return {
    latestInputTokens: latest,
    peakInputTokens: sawAny ? peak : null,
  };
}

/** Last roundtrip that recorded a system prompt and/or tool definitions snapshot. */
export function findLatestPrelude(rows: RoundtripUsageLike[]): RoundtripUsageLike | null {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i];
    if (row && (row.systemPromptText || row.toolDefinitionsJson)) {
      return row;
    }
  }
  return null;
}

export function countDistinctTurns(rows: RoundtripUsageLike[]): number {
  return new Set(rows.map((r) => r.turnNumber ?? r.seq)).size;
}

/**
 * One row per user-initiated turn.
 * Peak input = max call window; Input billed = sum of call inputs (spend).
 */
export function rollupRoundtripsByTurn(rows: RoundtripUsageLike[]): TurnRollupRow[] {
  const byTurn = new Map<number, RoundtripUsageLike[]>();
  for (const row of rows) {
    const turnNumber = row.turnNumber ?? row.seq;
    const bucket = byTurn.get(turnNumber);
    if (bucket) {
      bucket.push(row);
    } else {
      byTurn.set(turnNumber, [row]);
    }
  }
  return Array.from(byTurn.entries())
    .sort(([a], [b]) => a - b)
    .map(([turnNumber, group]) => {
      const sum = (pick: (r: RoundtripUsageLike) => number | null | undefined) =>
        group.reduce((acc, r) => acc + (Number(pick(r) ?? 0) || 0), 0);
      let peakInput = 0;
      for (const r of group) {
        const input = r.inputTokens ?? 0;
        if (input > peakInput) {
          peakInput = input;
        }
      }
      return {
        turnNumber,
        roundtrips: group.length,
        model: group[group.length - 1]?.model ?? null,
        traceId: group.find((row) => row.traceId)?.traceId ?? null,
        peakInputTokens: peakInput,
        inputBilled: sum((r) => r.inputTokens),
        outputTokens: sum((r) => r.outputTokens),
        thinkingTokens: sum((r) => r.thinkingTokens),
        cacheReadTokens: sum((r) => r.cacheReadTokens),
        cacheWriteTokens: sum((r) => r.cacheWriteTokens),
        estimatedCostUsd: sum((r) => r.estimatedCostUsd),
      };
    });
}

export function prettySpendBucketType(type: string): string {
  switch (type) {
    case "system_prompt":
      return "System prompt (× calls)";
    case "tool_definitions":
      return "Tool definitions (× calls)";
    case "conversation_history":
      return "History (× calls)";
    case "assistant_output":
      return "Assistant output";
    case "thinking":
      return "Thinking";
    case "compaction":
      return "Compaction summary";
    default:
      return type;
  }
}
